import { formatDateAsAnniversary } from 'helpers/dateTimes';

import type { FilterOption, PlanningTableRow } from './dynamicDeliveryColumns';

export type ViewMode = 'appro' | 'site' | 'map';
export type TableVariant = Exclude<ViewMode, 'map'>;

export const VIEW_OPTIONS: { id: ViewMode; label: string }[] = [
    { id: 'appro', label: 'Par appro' },
    { id: 'site', label: 'Par site' },
    { id: 'map', label: 'Carte' },
];

export const GROUP_BY: Record<TableVariant, string[]> = {
    appro: ['section', 'operatorGroup', 'regroupement'],
    site: ['section', 'siteGroup'],
};

// Always sort by section first so "Colonnes à livrer" stays above "Colonnes potentielles", whatever else the user sorts by.
export const SECTION_PINNED_SORT = [{ id: 'section', desc: false }];

// Cap the potential rows rendered by default: with several hundred candidates the grouped table DOM gets
// heavy, and only the best-ranked ones are actionable at a glance. An active search lifts the cap, so any
// potential column stays findable (the client-side filter only sees the rows we feed the table).
export const POTENTIAL_DISPLAY_LIMIT = 100;

export const LEGEND = [
    { token: 'errorColor', label: 'Critique' },
    { token: 'warningColor', label: 'Requis' },
    { token: 'successColor', label: 'OK' },
    { token: 'infoTextColor', label: 'Non communiqué' },
] as const;

export const startOfLocalDay = (d: Date): Date => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
};

export const isOpenBusinessDay = (date: Date, totemClosedDays: Array<{ date: string }>): boolean => {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return false;

    const dateString = formatDateAsAnniversary({ dateTime: date, useNewFormat: true });
    return !totemClosedDays.some(({ date: closedDate }) => closedDate === dateString);
};

export const addBusinessDays = (date: Date, daysToAdd: number, totemClosedDays: Array<{ date: string }>): Date => {
    const result = startOfLocalDay(date);
    let addedDays = 0;

    while (addedDays < daysToAdd) {
        result.setDate(result.getDate() + 1);
        if (isOpenBusinessDay(result, totemClosedDays)) {
            addedDays++;
        }
    }

    return result;
};

export const delivererName = (person: { username: string; profile: { fullname?: string | null } }): string =>
    person.profile.fullname ?? person.username;

// Distinct labels of the day's rows as sorted picker options (zone / operator filter chips).
export const collectFilterOptions = (
    rows: PlanningTableRow[],
    labelOf: (row: PlanningTableRow) => string | null | undefined,
): FilterOption[] =>
    [...new Set(rows.map(labelOf).filter((label): label is string => !!label))]
        .sort((a, b) => a.localeCompare(b, 'fr'))
        .map((label) => ({ value: label, label }));
